import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchUnitDashboardData, fetchUnitsData, getBaselineDailySummary } from "../../../../services/apis";
import { setAllUnitData } from "../../../../redux/Slice/UnitDirectory/UnitDirectorySlice";
import { setAllUnitsData } from "../../../../redux/Slice/SiteDirectory/SiteDirectorySlice";
import { useTheme } from "@mui/material/styles";
import {
  SettingsButton,
  CoolingEnergyButton,
  HeatingEnergyButton,
  BinDataTable,
  KPICardsUnit,
  InfoFooter,
  EditUnitSettingsModel,
  CoolingEnergyModel,
  HeatingEnergyModel,
  TakeUnitOnlineModel,
  BypassUnitModel,
  RebootSelectedUnit,
} from "../../../../features";
import { useParams } from "react-router-dom";

import { setBreadcrumbText, setHeaderText } from "../../../../redux/Slice/Navigation/NavigationSlice";
import { AlertBar } from "../../../../components";
import { RootState } from "../../../../redux/store";
import { UpdateUnitType } from "../../../../types/UnitDirectory/UnitDirectoryTypes";
import { selectUserRole } from "../../../../redux/Slice/Authentication/AuthenticationSlice";
import { UnitsRequestData } from "../../../../types";
import ChatbotContainer from "../../../../features/Chatbot/ChatbotContainer";

const defaultUpdateUnitPayload: UpdateUnitType = {
  unit_id: -1,
  unit_name: "",
  unit_serial_num: "",
  unit_brand: "",
  unit_model_number: "",
};

export default function UnitDashboard() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const dispatch = useDispatch();
  const userRole = useSelector(selectUserRole);
  const { projectName, siteName, siteId, unitName } = useParams();
  const [openEditUnitSettingsModel, setOpenEditUnitSettingsModel] = useState(false);
  const [openCoolingEnergyModel, setOpenCoolingEnergyModel] = useState(false);
  const [openHeatingEnergyModel, setOpenHeatingEnergyModel] = useState(false);
  const [openTakeOnlineUnitModel, setOpenTakeOnlineUnitModel] = useState(false);
  const [openBypassUnitModel, setOpenBypassUnitModel] = useState(false);
  const [openRebootUnitModel, setOpenRebootUnitModel] = useState(false);
  const [unitUpdatePayload, setUnitUpdatePayload] = useState<UpdateUnitType>(defaultUpdateUnitPayload);
  const pageParams = useParams();
  const siteData = useSelector((state: RootState) => state.siteDirectory.allUnitsData).data;
  // grab the specific unit selected from the site data
  const selectedUnit = siteData && siteData.find((unit: any) => unit.unit_id == pageParams.unitId);
  const [enableSearchQuery, setEnableSearchQuery] = useState(true);
  const [alertState, setAlertState] = useState({
    isAlert: false,
    severity: "",
    title: "",
    description: "",
    resetOpen: false,
  });
  const [siteUnitsRequestData, setSiteUnitsRequestData] = useState<UnitsRequestData>({
    search_substring: "",
    pageNumber: "0",
    pageSize: "0",
    sortOrder: "asc",
    sortField: "unit_name",
    site_Id: siteId || "",
  });
  const [value, setValue] = useState("offline");
  const [manualModelDetails, setManualModelDetails] = useState<any>(null);

  // if theres no sitedata we need to grab data to set to siteData here instead of re navigating
  const {
    data: siteUnitsData,
    isLoading: siteUnitsDataLoading,
    isError: siteUnitsDataError,
    isSuccess: siteUnitsDataSuccess,
    refetch: refetchSiteUnitsData,
  } = useQuery({
    queryKey: ["siteUnitsData", { siteUnitsRequestData }],
    queryFn: fetchUnitsData,
    enabled: enableSearchQuery,
    staleTime: 0,
  });

  useEffect(() => {
    if (!siteData) {
      setEnableSearchQuery(true);
    }
  }, []);

  useEffect(() => {
    // console.log("sites unit data------------------------------", siteUnitsData);
    if (!siteUnitsDataLoading && siteUnitsData !== undefined && !siteUnitsDataError) {
      setEnableSearchQuery(false);
      if (siteUnitsData.message === "No projects found") {
        dispatch(setAllUnitsData({}));
      } else {
        dispatch(setAllUnitsData(siteUnitsData));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteUnitsDataSuccess, siteUnitsData, dispatch]);
  // end sequence for grabbing data if siteData is not available

  const unitId = pageParams.unitId || "";

  const unitData = useSelector((state: RootState) => state.unitDirectory.unitData);
  // console.log("unitData", unitData);
  // const unitName = unitData.unit_name;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["unitDashboardData", { unitId }],
    queryFn: fetchUnitDashboardData,
    enabled: !!unitId || enableSearchQuery,
  });

  const { data: baselineDailySummaryData, isLoading: baselineDailySummaryDataLoading } = useQuery({
    queryKey: ["baselineDailySummaryData", { unitId }],
    queryFn: getBaselineDailySummary,
    enabled: !!unitId || enableSearchQuery,
  });

  // Log the data when it's available
  useEffect(() => {
    // console.log("Unit ID:", unitId);
    setEnableSearchQuery(false);
    if (data) {
      // console.log("Fetched Unit Dashboard Data:", data);
      dispatch(setAllUnitData(data)); // Store fetched data in Redux
      dispatch(setHeaderText(data.unit_name));
      dispatch(setBreadcrumbText(["Project Directory", projectName, siteName]));
    } else {
      dispatch(setBreadcrumbText(["Project Directory", projectName, siteName, "Unit Name"]));
    }
  }, [data, dispatch]);

  const resetAfterSubmit = () => {
    setOpenTakeOnlineUnitModel(false);
    setOpenBypassUnitModel(false);
    setOpenRebootUnitModel(false);
    setUnitUpdatePayload(defaultUpdateUnitPayload);
  };

  useEffect(() => {
    if (isError && !isLoading) {
      setAlertState({
        isAlert: true,
        severity: "error",
        title: "",
        description: "An error occurred while fetching data.",
        resetOpen: false,
      });
    }
  }, [isError, isLoading]);

  useEffect(() => {
    if (!baselineDailySummaryDataLoading && selectedUnit) {
      setManualModelDetails({
        cc: selectedUnit?.cc || 0,
        tccp: selectedUnit?.tcp || 0,
        bc: selectedUnit?.bc || 0,
        ch: selectedUnit?.ch || 0,
        thcp: selectedUnit?.thcp || 0,
        bh: selectedUnit?.bh || 0,
      });
    }
  }, [selectedUnit, baselineDailySummaryDataLoading]);

  // console.log("Fetched Baseline Daily Summary Data:", baselineDailySummaryData);
  // console.log("Baseline Daily Summary Data Loading:", baselineDailySummaryDataLoading);
  // console.log("Baseline Daily Summary Data Error:", baselineDailySummaryDataError);
  // console.log("selectedUnit", selectedUnit);
  // console.log("siteData", siteData);
  // console.log("siteUnitsData", siteUnitsData);

  if (isLoading || siteUnitsDataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: "40px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const setShowAlert = (show: boolean) => {
    setAlertState({ ...alertState, isAlert: show });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 250px)", // Adjust '100px' to match your footer height
          paddingX: {
            xs: "16px",
            sm: "16px",
            md: "24px",
          },
          paddingBottom: {
            xs: "16px",
            sm: "16px",
            md: "24px",
          },
          paddingTop: "4px",
        }}
      >
        {alertState.isAlert && (
          <>
            <AlertBar severity={alertState.severity} title={alertState.title} description={alertState.description} show={alertState.isAlert} setShow={setShowAlert} isFixed={true} />
            <Box
              sx={{
                marginTop: isSmallScreen ? "16px" : "32px",
              }}
            />
          </>
        )}
        <EditUnitSettingsModel
          openEditUnitSettingsModel={openEditUnitSettingsModel}
          setOpenEditUnitSettingsModel={setOpenEditUnitSettingsModel}
          setOpenTakeOnlineUnitModel={setOpenTakeOnlineUnitModel}
          setOpenBypassUnitModel={setOpenBypassUnitModel}
          setOpenRebootUnitModel={setOpenRebootUnitModel}
          unit={selectedUnit}
          refetchFunctions={[refetch, refetchSiteUnitsData]}
          value={value}
          setValue={setValue}
          setAlertState={setAlertState}
        />
        <CoolingEnergyModel
          openCoolingEnergyModel={openCoolingEnergyModel}
          setOpenCoolingEnergyModel={setOpenCoolingEnergyModel}
          baselineDailySummaryData={baselineDailySummaryData}
          unit={selectedUnit}
          manualModelDetails={manualModelDetails}
          setManualModelDetails={setManualModelDetails}
          setAlertState={setAlertState}
          setEnableSearchQuery={setEnableSearchQuery}
        />
        <HeatingEnergyModel
          openHeatingEnergyModel={openHeatingEnergyModel}
          setOpenHeatingEnergyModel={setOpenHeatingEnergyModel}
          baselineDailySummaryData={baselineDailySummaryData}
          unit={selectedUnit}
          manualModelDetails={manualModelDetails}
          setManualModelDetails={setManualModelDetails}
          setAlertState={setAlertState}
          setEnableSearchQuery={setEnableSearchQuery}
        />
        <TakeUnitOnlineModel
          unit={selectedUnit}
          setValue={setValue}
          openTakeOnlineUnitModel={openTakeOnlineUnitModel}
          setOpenTakeOnlineUnitModel={setOpenTakeOnlineUnitModel}
          setAlertState={setAlertState}
        />
        <BypassUnitModel unit={selectedUnit} setValue={setValue} openBypassUnitModel={openBypassUnitModel} setOpenBypassUnitModel={setOpenBypassUnitModel} />
        <RebootSelectedUnit unit={selectedUnit} setValue={setValue} openRebootUnitModel={openRebootUnitModel} setOpenRebootUnitModel={setOpenRebootUnitModel} />
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: {
              xs: "flex-start",
              sm: "flex-end",
            },
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          {userRole !== "TECHNICIAN" && <HeatingEnergyButton setOpenHeatingEnergyModel={setOpenHeatingEnergyModel} />}
          {userRole !== "TECHNICIAN" && <CoolingEnergyButton setOpenCoolingEnergyModel={setOpenCoolingEnergyModel} />}
          {userRole !== "TECHNICIAN" && <SettingsButton setOpenEditUnitSettingsModel={setOpenEditUnitSettingsModel} />}
        </Box>
        <Box sx={{ marginTop: "20px" }} />
        <BinDataTable />
        <Box sx={{ marginTop: "20px" }} />
        <KPICardsUnit projectName={projectName} siteName={siteName} unitName={unitName} userRole={userRole} setEnableSearchQuery={setEnableSearchQuery} />
      </Box>
      <ChatbotContainer chatType={"unit"} metadata={{ unitId: unitData?.unit_id || "" }} />
      <InfoFooter />
    </>
  );
}
